import { apiBaseUrl } from "./env";
import type { DownloadedFile } from "./browserDownload";
import type { SecureSessionStatus } from "./gboModels";

type ApiError = {
  status: number;
  message: string;
};

export type ApiFailure = {
  status?: number;
  message: string;
  name?: string;
  lockedUntil?: string | null;
};

export type GboRequestOptions = Omit<RequestInit, "body" | "credentials"> & {
  tenantId: string | null;
  body?: object;
};

type GboGetOptions = Omit<GboRequestOptions, "method" | "body">;

export const gboClient = {
  get<T>(path: string, options: GboGetOptions) {
    return requestJson<T>(path, { ...options, method: "GET" });
  },

  post<T>(path: string, options: Omit<GboRequestOptions, "method">) {
    return requestJson<T>(path, { ...options, method: "POST" });
  },

  request: requestJson,

  async file(
    path: string,
    options: GboGetOptions & { fallbackFileName: string },
  ): Promise<{ data: DownloadedFile } | { error: ApiFailure }> {
    const { fallbackFileName, ...request } = options;
    const response = await send(path, { ...request, method: "GET" }, "application/pdf");
    if (!response.ok) return { error: await readFailure(response) };

    return {
      data: {
        blob: await response.blob(),
        fileName:
          readContentDispositionFileName(response.headers.get("content-disposition")) ??
          fallbackFileName,
      },
    };
  },
};

function send(path: string, options: GboRequestOptions, accept: string) {
  const { tenantId, body, headers: additionalHeaders, ...init } = options;
  const headers = new Headers(additionalHeaders);
  headers.set("Accept", accept);
  if (tenantId !== null) headers.set("X-GBO-Tenant-Id", tenantId);
  if (body !== undefined) headers.set("Content-Type", "application/json");

  return fetch(`${apiBaseUrl}/gbo/${path}`, {
    ...init,
    credentials: "include",
    headers: Object.fromEntries(headers.entries()),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

async function requestJson<T>(
  path: string,
  options: GboRequestOptions,
): Promise<{ data: T } | { error: ApiFailure }> {
  const response = await send(path, options, "application/json");
  if (!response.ok) return { error: await readFailure(response) };

  return { data: (await response.json()) as T };
}

export function getApiFailureMessage(error: unknown, fallback: string) {
  return isApiFailure(error) || error instanceof Error ? error.message : fallback;
}

export function getApiFailureName(error: unknown) {
  return isApiFailure(error) ? error.name : undefined;
}

async function readFailure(response: Response): Promise<ApiFailure> {
  if (response.status === 404)
    return { status: response.status, name: "NotFoundError", message: "Not found" };

  if (response.status === 423) {
    const session = (await response.json()) as SecureSessionStatus;
    return {
      status: response.status,
      name: "LockedOutError",
      message: session.message,
      lockedUntil: session.lockedUntil,
    };
  }

  const error = await readError(response);
  return { status: response.status, message: error.message };
}

async function readError(response: Response): Promise<ApiError> {
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    const body = (await response.json()) as Partial<ApiError> & { message?: string };
    return {
      status: response.status,
      message: body.message ?? `Request failed with status ${response.status}`,
    };
  }

  const text = await response.text();
  return {
    status: response.status,
    message: text || `Request failed with status ${response.status}`,
  };
}

function readContentDispositionFileName(contentDisposition: string | null) {
  if (!contentDisposition) return null;

  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8Match) return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ""));

  const match = /filename=([^;]+)/i.exec(contentDisposition);
  return match?.[1].trim().replace(/^"|"$/g, "") ?? null;
}

function isApiFailure(error: unknown): error is ApiFailure {
  return (
    typeof error === "object" &&
    error != null &&
    "message" in error &&
    typeof error.message === "string"
  );
}
